import type { StyleDeltas } from '~/lib/stores/element-selector';
import { cssColorToHex, isTransparent } from './color-utils';

/*
 * Deterministic CSS-delta → Tailwind-class mapping for the style panel's 17
 * properties. Same contract the old agent prompt asked the LLM to follow —
 * "nearest scale token, else arbitrary value" — but computed in code so the
 * saved classes are exactly what the live preview showed. Any delta this can't
 * express returns null and the caller falls back to an agent turn, so partial
 * mappings never ship half an edit.
 */

/** px → Tailwind v3 default spacing suffix (margin/padding). */
const SPACING_PX: Record<number, string> = {
  0: '0',
  1: 'px',
  2: '0.5',
  4: '1',
  6: '1.5',
  8: '2',
  10: '2.5',
  12: '3',
  14: '3.5',
  16: '4',
  20: '5',
  24: '6',
  28: '7',
  32: '8',
  36: '9',
  40: '10',
  44: '11',
  48: '12',
  56: '14',
  64: '16',
  80: '20',
  96: '24',
  112: '28',
  128: '32',
  144: '36',
  160: '40',
  176: '44',
  192: '48',
  208: '52',
  224: '56',
  240: '60',
  256: '64',
  288: '72',
  320: '80',
  384: '96',
};

const FONT_SIZE_PX: Record<number, string> = {
  12: 'text-xs',
  14: 'text-sm',
  16: 'text-base',
  18: 'text-lg',
  20: 'text-xl',
  24: 'text-2xl',
  30: 'text-3xl',
  36: 'text-4xl',
  48: 'text-5xl',
  60: 'text-6xl',
  72: 'text-7xl',
  96: 'text-8xl',
  128: 'text-9xl',
};

const FONT_WEIGHT: Record<number, string> = {
  100: 'font-thin',
  200: 'font-extralight',
  300: 'font-light',
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
  800: 'font-extrabold',
  900: 'font-black',
};

const BORDER_WIDTH_PX: Record<number, string> = {
  0: 'border-0',
  1: 'border',
  2: 'border-2',
  4: 'border-4',
  8: 'border-8',
};

const BORDER_RADIUS_PX: Record<number, string> = {
  0: 'rounded-none',
  2: 'rounded-sm',
  4: 'rounded',
  6: 'rounded-md',
  8: 'rounded-lg',
  12: 'rounded-xl',
  16: 'rounded-2xl',
  24: 'rounded-3xl',
};

const TEXT_ALIGN = new Set(['left', 'center', 'right', 'justify']);

/** Tailwind's opacity scale (v3 defaults), keyed by percent. */
const OPACITY_PCT = new Set([0, 5, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 95, 100]);

/** "18px" / "0" → 18 / 0; anything else (auto, %, em) → null. */
function parsePx(value: string): number | null {
  const m = value.trim().match(/^(-?\d+(?:\.\d+)?)(px)?$/);

  if (!m) {
    return null;
  }

  const n = parseFloat(m[1]);

  return Number.isFinite(n) ? n : null;
}

/** Arbitrary-value bodies can't contain spaces or brackets. */
function arbitrary(prefix: string, body: string): string | null {
  return /[\s[\]]/.test(body) ? null : `${prefix}-[${body}]`;
}

function spacingClass(prefix: string, value: string): string | null {
  const px = parsePx(value);

  if (px === null) {
    return null;
  }

  const negative = px < 0;
  const token = SPACING_PX[Math.abs(px)];

  if (token !== undefined) {
    return negative ? `-${prefix}-${token}` : `${prefix}-${token}`;
  }

  return arbitrary(prefix, `${px}px`);
}

function colorClass(prefix: string, value: string): string | null {
  if (isTransparent(value)) {
    return `${prefix}-transparent`;
  }

  const hex = cssColorToHex(value);

  return hex ? `${prefix}-[${hex}]` : null;
}

/** One panel delta → one Tailwind class, or null when it can't be expressed. */
export function deltaToClass(prop: string, value: string): string | null {
  switch (prop) {
    case 'font-size': {
      const px = parsePx(value);

      if (px === null) {
        return null;
      }

      return FONT_SIZE_PX[px] ?? arbitrary('text', `${px}px`);
    }
    case 'font-weight': {
      const n = parseInt(value, 10);

      if (!Number.isFinite(n)) {
        return null;
      }

      return FONT_WEIGHT[n] ?? arbitrary('font', String(n));
    }
    case 'text-align':
      return TEXT_ALIGN.has(value.trim()) ? `text-${value.trim()}` : null;
    case 'margin-top':
      return spacingClass('mt', value);
    case 'margin-right':
      return spacingClass('mr', value);
    case 'margin-bottom':
      return spacingClass('mb', value);
    case 'margin-left':
      return spacingClass('ml', value);
    case 'padding-top':
      return spacingClass('pt', value);
    case 'padding-right':
      return spacingClass('pr', value);
    case 'padding-bottom':
      return spacingClass('pb', value);
    case 'padding-left':
      return spacingClass('pl', value);
    case 'border-color':
      return colorClass('border', value);
    case 'border-width': {
      const px = parsePx(value);

      if (px === null || px < 0) {
        return null;
      }

      return BORDER_WIDTH_PX[px] ?? arbitrary('border', `${px}px`);
    }
    case 'border-radius': {
      const px = parsePx(value);

      if (px === null || px < 0) {
        return null;
      }

      if (px >= 9999) {
        return 'rounded-full';
      }

      return BORDER_RADIUS_PX[px] ?? arbitrary('rounded', `${px}px`);
    }
    case 'color':
      return colorClass('text', value);
    case 'background-color':
      return colorClass('bg', value);
    case 'opacity': {
      const n = parseFloat(value);

      if (!Number.isFinite(n) || n < 0 || n > 1) {
        return null;
      }

      const pct = Math.round(n * 100);

      return OPACITY_PCT.has(pct) && Math.abs(n * 100 - pct) < 0.001
        ? `opacity-${pct}`
        : arbitrary('opacity', String(n));
    }
    default:
      return null;
  }
}

export type MappedDeltas = { ok: true; classes: string[] } | { ok: false; unmappable: string };

/** All deltas or nothing — a partially-applied style save would silently drop edits. */
export function deltasToClasses(deltas: StyleDeltas): MappedDeltas {
  const classes: string[] = [];

  for (const [prop, delta] of Object.entries(deltas)) {
    const cls = deltaToClass(prop, delta.value);

    if (!cls) {
      return { ok: false, unmappable: prop };
    }

    classes.push(cls);
  }

  return { ok: true, classes };
}
